import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
  Brush,
  Legend,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { getPatientHealthIndexHistory } from '../services/api';
import { SyntheticInputsNotice } from './DemoDataBadge';

interface HealthIndexChartProps {
  patientId: string;
}

type TimeRange = 'hours' | 'day' | 'week' | 'month';

const TIME_RANGES: { value: TimeRange; label: string; hours: number }[] = [
  { value: 'hours', label: 'Last Hour', hours: 1 },
  { value: 'day', label: 'Last 24h', hours: 24 },
  { value: 'week', label: 'Last Week', hours: 168 },
  { value: 'month', label: 'Last Month', hours: 720 },
];

const SCORE_COLORS: Record<number, string> = {
  0: '#10b981', // green
  1: '#3b82f6', // blue
  2: '#eab308', // yellow
  3: '#f97316', // orange
  4: '#ef4444', // red
  5: '#dc2626', // dark red
};

const getScoreColor = (score: number): string => {
  if (score <= 0.5) return SCORE_COLORS[0];
  if (score <= 1.5) return SCORE_COLORS[1];
  if (score <= 2.5) return SCORE_COLORS[2];
  if (score <= 3.5) return SCORE_COLORS[3];
  if (score <= 4.5) return SCORE_COLORS[4];
  return SCORE_COLORS[5];
};

const HealthIndexChart: React.FC<HealthIndexChartProps> = ({ patientId }) => {
  const [timeRange, setTimeRange] = useState<TimeRange>('day');
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadHistory();
  }, [patientId, timeRange]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const hours = TIME_RANGES.find(r => r.value === timeRange)?.hours || 24;
      const data = await getPatientHealthIndexHistory(patientId, hours);
      const scores = data.scores || [];

      const formatted = scores
        .sort((a: any, b: any) => new Date(a.calculated_at).getTime() - new Date(b.calculated_at).getTime())
        .map((s: any) => ({
          timestamp: new Date(s.calculated_at).getTime(),
          time: s.calculated_at,
          // score is NUMERIC in Postgres and arrives as a STRING, and the mock
          // scorer returns no rawScore at all — so this fell back to a string and
          // `.toFixed()` below threw, blanking the whole patient page. `?.` guards
          // null, not the wrong type: coerce here, where the field is built.
          score: Math.min(5, Math.max(0, Math.round(Number(s.score)))),
          rawScore: Number(s.raw_response?.rawScore ?? s.score),
          confidence: typeof s.confidence === 'string' ? parseFloat(s.confidence) : s.confidence,
          isFinal: s.raw_response?.isFinal ?? false,
          alert: s.raw_response?.alert ?? false,
          formattedTime: formatTime(s.calculated_at),
          syntheticInputs: (s.synthetic_inputs as string[] | undefined) ?? [],
        }));

      setChartData(formatted);
    } catch (err) {
      setError('Failed to load health index history');
      console.error('Error loading health index history:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp: string): string => {
    const date = parseISO(timestamp);
    switch (timeRange) {
      case 'hours':
        return format(date, 'HH:mm:ss');
      case 'day':
        return format(date, 'HH:mm');
      case 'week':
        return format(date, 'MMM d, HH:mm');
      case 'month':
        return format(date, 'MMM d');
      default:
        return format(date, 'HH:mm');
    }
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    const scoreColor = getScoreColor(d.rawScore);
    const levelLabels = ['Stable', 'Minor', 'Moderate', 'Significant', 'Major', 'Critical'];

    return (
      <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
        <p className="font-semibold text-gray-800 mb-1">
          {format(parseISO(d.time), 'MMM d, yyyy HH:mm:ss')}
        </p>
        <p className="text-sm">
          <span className="font-medium" style={{ color: scoreColor }}>
            VSI Score: {d.rawScore.toFixed(1)}
          </span>
          <span className="text-gray-500 ml-2">({levelLabels[d.score]})</span>
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Confidence: {d.confidence?.toFixed(0)}%
          {d.isFinal && <span className="ml-2 text-green-600">Validated</span>}
          {d.alert && <span className="ml-2 text-red-600">Alert</span>}
        </p>
      </div>
    );
  };

  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (!payload) return null;
    const color = getScoreColor(payload.rawScore);
    return (
      <circle
        cx={cx}
        cy={cy}
        r={payload.alert ? 6 : 4}
        fill={color}
        stroke={payload.alert ? '#dc2626' : 'white'}
        strokeWidth={payload.alert ? 2 : 1}
      />
    );
  };

  if (loading && chartData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Health Index Trend</h3>
        </div>
        <div className="animate-pulse h-64 bg-gray-100 rounded" />
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Health Index Trend</h3>
          <div className="flex space-x-2">
            {TIME_RANGES.map(range => (
              <button
                key={range.value}
                onClick={() => setTimeRange(range.value)}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  timeRange === range.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>
        <div className="text-center text-gray-500 py-12">
          No health index history available for this time range.
          <br />
          <span className="text-sm">Calculate a health index to start tracking trends.</span>
        </div>
      </div>
    );
  }

  const rawScores = chartData.map(d => d.rawScore);
  const latest = chartData[chartData.length - 1];
  // Any modality that was synthetic in ANY point of the series taints the trend.
  const syntheticModalities: string[] = Array.from(
    new Set<string>(chartData.flatMap((d: any) => (d.syntheticInputs as string[]) ?? []))
  );

  const avg = rawScores.reduce((s, v) => s + v, 0) / rawScores.length;
  const min = Math.min(...rawScores);
  const max = Math.max(...rawScores);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <SyntheticInputsNotice modalities={syntheticModalities} />

      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Health Index Trend</h3>
        <div className="flex space-x-2">
          {TIME_RANGES.map(range => (
            <button
              key={range.value}
              onClick={() => setTimeRange(range.value)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                timeRange === range.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 30 }}>
          <defs>
            <linearGradient id="gradient-vsi" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

          <XAxis
            dataKey="formattedTime"
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
          />

          <YAxis
            domain={[0, 5]}
            ticks={[0, 1, 2, 3, 4, 5]}
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
            label={{ value: 'VSI Score', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
          />

          <Tooltip content={<CustomTooltip />} />

          <Legend wrapperStyle={{ fontSize: '14px' }} iconType="line" />

          {/* Score level bands */}
          <ReferenceArea y1={0} y2={1} fill="#10b981" fillOpacity={0.06} />
          <ReferenceArea y1={1} y2={2} fill="#3b82f6" fillOpacity={0.06} />
          <ReferenceArea y1={2} y2={3} fill="#eab308" fillOpacity={0.06} />
          <ReferenceArea y1={3} y2={5} fill="#ef4444" fillOpacity={0.08} />

          {/* Clinical attention threshold */}
          <ReferenceLine y={3} stroke="#ef4444" strokeDasharray="5 5" label="Clinical Review" />

          <Line
            type="monotone"
            dataKey="rawScore"
            stroke="#6366f1"
            strokeWidth={2.5}
            dot={<CustomDot />}
            activeDot={{ r: 6, fill: '#6366f1' }}
            fill="url(#gradient-vsi)"
            name="VSI Score"
            animationDuration={800}
            animationEasing="ease-in-out"
          />

          <Brush
            dataKey="formattedTime"
            height={30}
            stroke="#6366f1"
            fill="#f3f4f6"
            travellerWidth={10}
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Stats summary */}
      <div className="mt-4 grid grid-cols-4 gap-4 text-center">
        <div className="bg-gray-50 rounded p-2">
          <p className="text-xs text-gray-600">Latest</p>
          <p className="text-lg font-semibold" style={{ color: getScoreColor(latest?.rawScore || 0) }}>
            {latest?.rawScore?.toFixed(1) ?? '-'}
          </p>
        </div>
        <div className="bg-gray-50 rounded p-2">
          <p className="text-xs text-gray-600">Average</p>
          <p className="text-lg font-semibold text-gray-700">{avg.toFixed(1)}</p>
        </div>
        <div className="bg-gray-50 rounded p-2">
          <p className="text-xs text-gray-600">Min</p>
          <p className="text-lg font-semibold text-green-600">{min.toFixed(1)}</p>
        </div>
        <div className="bg-gray-50 rounded p-2">
          <p className="text-xs text-gray-600">Max</p>
          <p className="text-lg font-semibold text-red-600">{max.toFixed(1)}</p>
        </div>
      </div>

      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </div>
  );
};

export default HealthIndexChart;
